import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { isEmpty, prop, path, compose, __ } from 'ramda'
import {
  PRIMARY_FILTER,
  SECONDARY_FILTER,
  COMMON_DELIMITER,
} from 'utils/constants'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { handleRemoveFilterTerm, handleClearFilterTerms } from 'actions/search'
import {
  getFiltersByCollection,
  getFilterDataByCollection,
  getFilterSelectionByCollection,
} from 'selectors/search'
import ChipList from 'components/ChipList'
import {
  AdvancedSearchFiltersHeader,
  AdvancedFiltersTitle,
  ClearFilters,
  StyledSelectMenu,
  AdvancedSearchOuterWrap,
  AdvancedSearchWrap,
} from './AdvancedSearchFilters.style'

/* eslint-disable react-hooks/exhaustive-deps */
const AdvancedSearchFilters = ({
  collectionName,
  hideChips,
  onChange,
  handleClearFilters,
  hideSecondaryFilterIfEmpty,
}) => {
  const dispatch = useDispatch()
  const [groupOrder, setGroupOrder] = useState([])
  const filterGroups = useSelector(getFilterDataByCollection, collectionName)
  const activeFilters = useSelector(getFiltersByCollection, collectionName)

  useEffect(() => {
    if (filterGroups?.groupOrder) {
      setGroupOrder(filterGroups?.groupOrder)
    }
  }, [filterGroups])

  const hasFilters = filterGroups && Object.keys(filterGroups).length > 0
  const primarySelection = useSelector(getFilterSelectionByCollection, {
    filterKey: 'filterSelection1',
    collectionName,
  })
  const secondarySelection = useSelector(getFilterSelectionByCollection, {
    filterKey: 'filterSelection2',
    collectionName,
  })

  const handleChipRemove = (value) => {
    dispatch(handleRemoveFilterTerm(value, collectionName))
  }

  const handleClearFiltersDefault = () => {
    dispatch(handleClearFilterTerms(collectionName))
  }

  const clearFiltersOnClick = () =>
    handleClearFilters || handleClearFiltersDefault

  const getIsDisabled = compose(prop(__, activeFilters), prop('value'))

  const Filters = useMemo(() => {
    // look into putting this into selectors
    // delete filterGroups.groupOrder
    const primary =
      groupOrder &&
      groupOrder.reduce((acc, key) => {
        const curr = filterGroups[key]
        if (curr) {
          acc.push(
            ...Object.entries(curr.descriptions).map(([descKey, value]) => ({
              title: value,
              value: `${curr.groupName}`,
              key: descKey,
            }))
          )
        }
        return acc
      }, [])

    // look into putting this into selectors
    const [group, key] = primarySelection
      ? primarySelection.split(COMMON_DELIMITER)
      : []
    const secondary = Object.values(
      prop('filters', filterGroups[group]) || {}
    ).map((filter) => ({
      title: path(['descriptions', key], filter),
      value: prop('value', filter),
      disabled: getIsDisabled(filter),
    }))

    const loading = isEmpty(primary)

    const showSecondaryFilterDropdown = hideSecondaryFilterIfEmpty
      ? !isEmpty(secondary)
      : true

    return (
      <>
        <div>
          <StyledSelectMenu
            loading={loading}
            vertical
            value={primarySelection}
            onChange={(val) => onChange(val, PRIMARY_FILTER)}
            showSearch
            filterOption
            placeholder={strings.select_criteria}
            dropdownMatchzselectWidth
            style={{ width: '100%' }}
            options={primary}
          />
          {showSecondaryFilterDropdown && (
            <StyledSelectMenu
              loading={loading}
              disabled={isEmpty(secondary)}
              vertical
              value={secondarySelection}
              onChange={(val) => onChange(val, SECONDARY_FILTER)}
              showSearch
              filterOption
              placeholder={strings.select_value}
              dropdownMatchzselectWidth
              style={{ width: '100%' }}
              options={secondary}
            />
          )}
        </div>
        <div>
          {!hideChips && !isEmpty(activeFilters) && (
            <ChipList chips={activeFilters} remove={handleChipRemove} />
          )}
        </div>
      </>
    )
  }, [
    filterGroups,
    primarySelection,
    secondarySelection,
    activeFilters,
    handleChipRemove,
  ])

  const activeFiltersLength = useMemo(
    () => (!isEmpty(activeFilters) ? Object.values(activeFilters).length : -1),
    [activeFilters]
  )

  const advancedFiltersTitle = (
    <>
      {
        <AdvancedFiltersTitle>{`${strings.advaned_filters} ${
          activeFiltersLength > -1 ? `(${activeFiltersLength})` : ``
        }`}</AdvancedFiltersTitle>
      }
      {activeFiltersLength > -1 && (
        <ClearFilters onClick={clearFiltersOnClick()}>
          {strings.clear}
        </ClearFilters>
      )}
    </>
  )

  if (!hasFilters) return null

  return (
    <AdvancedSearchOuterWrap>
      <AdvancedSearchWrap>
        <AdvancedSearchFiltersHeader>
          {advancedFiltersTitle}
        </AdvancedSearchFiltersHeader>
        {Filters}
      </AdvancedSearchWrap>
    </AdvancedSearchOuterWrap>
  )
}

export default AdvancedSearchFilters

AdvancedSearchFilters.propTypes = {
  setFilters: PropTypes.func,
  applyFilters: PropTypes.func,
  isSearching: PropTypes.bool,
  searchString: PropTypes.string,
  collectionName: PropTypes.string,
  hideChips: PropTypes.bool,
  onChange: PropTypes.func,
  handleClearFilters: PropTypes.func,
  hideSecondaryFilterIfEmpty: PropTypes.bool,
}
